import { AuthApi, Configuration } from '@/requests/mainApi';
import { getAccessToken, getRefreshToken, getTemporaryAccessToken, setTokens } from '@/utils/plugins/jwt';
import { waitingTheCondition } from '@/utils/helpers/helpFunctions';
import router from '@/lib/router';

export interface IResponse<T = never> {
  status: boolean;
  code: number;
  body?: T;
  errorMessage?: string;
}

/**
 * @description Достаёт текст ошибки из ответа бэкенда формата {detail, errorMessage}.
 * Без этого причина отказа (например, 403 «Ваш аккаунт был заблокирован») теряется
 * и показать пользователю нечего.
 * @param error - объект ошибки; при ошибке ответа это Response кодгена
 * @return Promise<string | undefined> - текст для пользователя, если бэк его прислал
 */
export const readErrorMessage = async (error: any): Promise<string | undefined> => {
  if (typeof error?.json !== 'function') {
    return undefined;
  }

  try {
    const body = await error.json();

    return body?.errorMessage || body?.detail || undefined;
  } catch {
    return undefined;
  }
};


function getBasePath(): string {
  if (process.env.VUE_APP_API_URL) {
    return process.env.VUE_APP_API_URL;
  } else if (process.env.NODE_ENV === 'development') {
    return 'https://abdlk-back.netimob.com/api';
  }

  return 'https://lk.akbars-dom.ru/api';
}

export const BASE_PATH = getBasePath();

class NonExistMethodException extends Error {
  constructor(constructorName: string, methodName: string) {
    super();
    this.message = `Method ${methodName} is not exist in ${constructorName} constructor. Check this`;
    this.name = 'NonExistMethodError';
  }
}

// eslint-disable-next-line
class ErrorTokenException extends Error {
  constructor() {
    super();
    this.message = 'The token is invalid or expired';
    this.name = 'TokenError';
  }
}

/**
 * @description Основной метод реквест машины, точка входа
 * @typeParam <T, TVariables> - Дженерики функции
 * @param RequestClass: any - Соответствующий класс апи кодгена, для создания запроса к серверу
 * @param methodName: string - Наименование метода данного экзепляра класса,
 * вызываемоего для осуществления запроса
 * @param withJWT: boolean - Флаг необходимости использования токена в запросе
 * @param variables?: TVariables - Аргументы запроса
 * @return Promise<IResponse<T>> - Объект класса IResponse с полем body типа T,
 * если запрос прошёл удачно и без поля body, если запрос завершился с ошибкой
 */
export const requestMachine = async <T = any, TVariables = never>(
  RequestClass: any,
  methodName: string,
  withJWT: boolean,
  variables?: TVariables
): Promise<IResponse<T>> => {
  try {
    // Проверка на запущенное обновление токена
    await waitingTheCondition(() => getAccessToken() !== 'updating', 200);
    // Проверка валидности токена, если он используется в запросе
    // if (withJWT && !jwtCheck(getAccessToken())) {
    //   throw new ErrorTokenException()
    // }
    // Построение и выполнение запроса
    const response = await requestBuilder<T, TVariables>(RequestClass, methodName, withJWT, variables);
    // Запись device-id для последующего использования в запросах (так надо было бэкам)
    if (getDeviceId() !== response.raw.headers.get('x-device-id') && response.raw.headers.get('x-device-id')) {
      localStorage.setItem('deviceId', response.raw.headers.get('x-device-id') || '');
    }

    return {
      code: response.raw.status,
      status: response.raw.ok,
      body: await response.value(),
    };
  } catch (e) {
    console.log('REQUEST ERROR', e);
    // Ошибка: Token not valid
    if ((e.status === 401 && e.statusText === 'Unauthorized') || e.name === 'TokenError') {
      await refreshToken();
      const response = await requestBuilder<T, TVariables>(RequestClass, methodName, withJWT, variables);

      return {
        code: response.raw.status,
        status: response.raw.ok,
        body: await response.value(),
      };
    }
    // Ошибка: Ошибка респонса (ошибка возвращаемая в ответе бэка)
    return {
      code: e.status,
      status: e.ok,
      errorMessage: await readErrorMessage(e),
    };
  }
};

/**
 * @description Построитель запроса, вспомогательный метод реквест машины
 * @typeParam <T, TVariables> - Дженерики функции
 * @param RequestClass: any - Соответствующий класс апи кодгена, для создания запроса к серверу
 * @param methodName: string - Наименование метода данного экзепляра класса,
 * вызываемоего для осуществления запроса
 * @param withJWT: boolean - Флаг необходимости использования токена в запросе
 * @param variables?: TVariables - Аргументы запроса
 * @return any - Метод methodName класса RequestClass c аргументами variables
 */
const requestBuilder = async <T = any, TVariables = never>(
  RequestClass: any,
  methodName: string,
  withJWT: boolean,
  variables?: TVariables
): Promise<any> => {
  try {
    // Конфигурация для openapi кодгена
    const configuration = new Configuration({
      basePath: BASE_PATH,
      apiKey: computeApiFunction(withJWT, methodName === 'authV1ProfileRead' || methodName === 'authV1ConfirmEmail'),
    });
    // Создание экемпляра класса применяемого раздела API
    const request = new RequestClass(configuration);
    // Проверка существования метода
    if (!request[methodName]) {
      throw new NonExistMethodException(RequestClass.name, methodName);
    }
    // Построение запроса
    return variables ? request[methodName + 'Raw'](variables) : request[methodName + 'Raw']();
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/**
 * @description Метод обновления пары токенов по refresh токену
 */
const refreshToken = async (): Promise<void> => {
  try {
    const refresh = getRefreshToken();
    setTokens('updating', refresh || '');

    // Проверка валидности токена, если он используется в запросе
    // if (!jwtCheck(refresh)) {
    //   console.log('1111');
    //   throw new ErrorTokenException()
    // }
    // Конфигурация для openapi кодгена
    const configuration = new Configuration({
      basePath: BASE_PATH,
      apiKey: computeApiFunction(true),
    });
    // Запрос на обновление пары токенов
    const request = await new AuthApi(configuration).authV1TokenRefreshCreate({
      data: {
        refresh: refresh!,
      },
    });
    // Обновление пары токенов в localStorage и сторе
    setTokens(request.access, request.refresh);
  } catch (e) {
    // Обнуление пары токенов в localStorage и сторе и редирект на форму авторизации
    setTokens('', '');
    router.push('/sign-in');
  }
};

/**
 * @description Фабрика для создания функции, возвращающей значие по ключу (нужно для apiKey в кодгене)
 * @param withJWT: boolean - Флаг необходимости использования токена в запросе
 * @return function - Метод methodName класса RequestClass c аргументами variables
 */
const computeApiFunction = (withJWT: boolean, useTemporaryToken: boolean = false): ((name: string) => string) => {
  return (name: string): string => {
    const savedToken = getAccessToken();

    const token = useTemporaryToken && !savedToken ? getTemporaryAccessToken() : savedToken;
    const keys: { [key: string]: any } = {
      'X-Device-Type': 'web',
      'X-Device-Id': getDeviceId(),
      Authorization: withJWT ? `Bearer ${token}` : '',
    };
    return keys[name] || '';
  };
};

export const getDeviceId = () => {
  return localStorage.getItem('deviceId') || '';
};
